import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { ParagraphModel } from "@common/utils/redline/ooxml/paragraph-model";

import { type DocumentPage, EMPTY_DOCUMENT_PAGE } from "../../../utils/anchor/document-page-store";
import type { PageCommentRead } from "../../../utils/anchor/word-document";
import { removedRowKeysOfBookmarks } from "../../../utils/redline-row-removed";

export type PageRevisionKind = "inserted" | "deleted";

export interface PageRevisionRun {
  kind: PageRevisionKind;
  author: string;
  text: string;
  acceptedStart: number;
  acceptedEnd: number;
}

export interface LivePageParagraph {
  text: string;
  revisions: PageRevisionRun[];
}

export interface LivePageSnapshot {
  completed: boolean;
  commentById: ReadonlyMap<string, PageCommentRead>;
  paragraphs: ReadonlyMap<string, LivePageParagraph>;
  tableIdByBookmark: ReadonlyMap<string, string>;
  tablePackageByGroupKey: ReadonlyMap<string, string>;
  removedRowKeyByBookmark: ReadonlyMap<string, string>;
  documentOrder: ReadonlyMap<string, number>;
  presentBookmarks: ReadonlySet<string>;
  coveredBookmarks: ReadonlySet<string>;
  readError: string | null;
  readStartedAtMs: number;
}

const TABLE_GROUP_KEY_PREFIX = "table:";

export const tableGroupKeyOf = (tableId: string): string => `${TABLE_GROUP_KEY_PREFIX}${tableId}`;

export const isTableGroupKey = (groupKey: string): boolean => groupKey.startsWith(TABLE_GROUP_KEY_PREFIX);

export function livePageParagraphOf(paragraphOoxml: string): LivePageParagraph | null {
  try {
    const range = new OoxmlRange(paragraphOoxml);
    if (range.paragraphCount === 0) return null;
    const pieces = new ParagraphModel(range.paragraphElement(0)).pieces;
    const revisions: PageRevisionRun[] = [];
    let text = "";
    for (const piece of pieces) {
      if (piece.deletedBy !== null) {
        revisions.push({ kind: "deleted", author: piece.deletedBy, text: piece.text, acceptedStart: text.length, acceptedEnd: text.length });
        continue;
      }
      if (piece.insertedBy !== null) revisions.push({ kind: "inserted", author: piece.insertedBy, text: piece.text, acceptedStart: text.length, acceptedEnd: text.length + piece.text.length });
      text += piece.text;
    }
    return { text, revisions };
  } catch {
    return null;
  }
}

function removedRowKeysOf(page: DocumentPage, tableIdByBookmark: ReadonlyMap<string, string>): Map<string, string> {
  const removedRowKeyByBookmark = new Map<string, string>();
  for (const [tableId, table] of page.tables) {
    const bookmarksInTable = Array.from(tableIdByBookmark.entries()).filter(([, bookmarkTableId]) => bookmarkTableId === tableId).map(([bookmarkName]) => bookmarkName);
    try {
      for (const [bookmarkName, rowKey] of removedRowKeysOfBookmarks(table.packageOoxml, bookmarksInTable)) removedRowKeyByBookmark.set(bookmarkName, `${tableId}:${rowKey}`);
    } catch {
      continue;
    }
  }
  return removedRowKeyByBookmark;
}

export function livePageSnapshotOf(page: DocumentPage): LivePageSnapshot {
  const paragraphs = new Map<string, LivePageParagraph>();
  const tableIdByBookmark = new Map<string, string>();
  const tablePackageByGroupKey = new Map<string, string>();
  const documentOrder = new Map<string, number>();
  for (const [bookmarkName, read] of page.paragraphs) {
    if (read.tableId !== null) tableIdByBookmark.set(bookmarkName, read.tableId);
    documentOrder.set(bookmarkName, read.paragraphIndex);
    const groupKey = tableGroupKeyOf(read.tableId ?? bookmarkName);
    documentOrder.set(groupKey, Math.min(documentOrder.get(groupKey) ?? Number.POSITIVE_INFINITY, read.paragraphIndex));
    const tablePackage = read.tableId === null ? read.paragraphOoxml : page.tables.get(read.tableId)?.packageOoxml;
    if (tablePackage !== undefined && !tablePackageByGroupKey.has(groupKey)) tablePackageByGroupKey.set(groupKey, tablePackage);
    const pageParagraph = livePageParagraphOf(read.paragraphOoxml);
    if (pageParagraph !== null) paragraphs.set(bookmarkName, pageParagraph);
  }
  return {
    completed: page.completed,
    commentById: page.commentById,
    paragraphs,
    tableIdByBookmark,
    tablePackageByGroupKey,
    removedRowKeyByBookmark: removedRowKeysOf(page, tableIdByBookmark),
    documentOrder,
    presentBookmarks: new Set(page.paragraphs.keys()),
    coveredBookmarks: page.coveredBookmarks,
    readError: page.lastReadError,
    readStartedAtMs: page.readStartedAtMs,
  };
}

export const EMPTY_PAGE_SNAPSHOT: LivePageSnapshot = livePageSnapshotOf(EMPTY_DOCUMENT_PAGE);

export function pageAuthorsOf(revisions: PageRevisionRun[]): string[] {
  return Array.from(new Set(revisions.map((run) => run.author)));
}
